import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import type { Nullable, TableTypes } from '../../common/types';
import { type FarmOptionsTypes } from '../../components/CustomizeColumns/types';
import type { SettingsTypes } from './types';
import { UI_DATE_FORMAT } from '../../common/constants';

export const getBasicsDefaultFilters = (): FarmOptionsTypes.Selection[] => {
  return [
    {
      key: 'node',
      name: 'Node name',
      searchable: true,
      order: 1,
    },
    {
      key: 'engineeringName',
      name: 'Engineering name',
      searchable: true,
      order: 2,
    },
    {
      key: 'settingRole',
      name: 'Role',
      searchable: true,
      order: 3,
    },
    {
      key: 'default_value',
      name: 'Default value',
      searchable: true,
      order: 4,
    },
    {
      key: 'unit',
      name: 'Unit',
      searchable: true,
      order: 5,
    },
    {
      key: 'description',
      name: 'Description',
      searchable: true,
      order: 6,
    },
  ];
};

export const getBasicsDefaultFiltersForAMF =
  (): FarmOptionsTypes.Selection[] => {
    return [
      {
        key: 'node',
        name: 'Node name',
        searchable: true,
        order: 1,
      },
      {
        key: 'engineeringName',
        name: 'Engineering name',
        searchable: true,
        order: 2,
      },
      {
        key: 'default_value',
        name: 'Default value',
        searchable: true,
        order: 3,
      },
      {
        key: 'unit',
        name: 'Unit',
        searchable: true,
        order: 4,
      },
    ];
  };

export const getAMFSettingsBasicFilters = (): FarmOptionsTypes.Selection[] => {
  return [
    {
      key: 'node',
      name: 'Node name',
      searchable: true,
      order: 1,
    },
    {
      key: 'engineeringName',
      name: 'Engineering name',
      searchable: true,
      order: 2,
    },
    {
      key: 'settingRole',
      name: 'Role',
      searchable: true,
      order: 3,
    },
    {
      key: 'default_value',
      name: 'Default value',
      searchable: true,
      order: 4,
    },
    {
      key: 'unit',
      name: 'Unit',
      searchable: true,
      order: 5,
    },
    {
      key: 'description',
      name: 'Description',
      searchable: true,
      order: 6,
    },

    {
      key: 'default',
      name: 'Default',
      order: 7,
    },
  ];
};

export const getVectorSettingsBasicFilters =
  (): FarmOptionsTypes.Selection[] => {
    return [
      {
        key: 'deviceType',
        name: 'Device type',
        searchable: true,
        order: 1,
      },
      {
        key: 'name',
        name: 'Name',
        searchable: true,
        order: 2,
      },
      {
        key: 'node',
        name: 'Node name',
        searchable: true,
        order: 3,
      },
      {
        key: 'settingRole',
        name: 'Role',
        searchable: true,
        order: 4,
      },
      {
        key: 'default_value',
        name: 'Default value',
        searchable: true,
        order: 5,
      },
      {
        key: 'unit',
        name: 'Unit',
        searchable: true,
        order: 6,
      },
      {
        key: 'previousValue',
        name: `Previous value`,
        searchable: true,
        order: 7,
      },
      {
        key: 'dateChanged',
        name: 'Date changed',
        searchable: true,
        order: 8,
      },
      {
        key: 'description',
        name: 'Description',
        searchable: true,
        order: 9,
      },

      {
        key: 'default',
        name: 'Default',
        order: 10,
      },
    ];
  };

export const getAstronautSettingsBasicFilters = (
  entityName: string,
): FarmOptionsTypes.Selection[] => {
  return [
    {
      key: 'location',
      name: 'Location',
      searchable: true,
      order: 1,
    },
    {
      key: 'name',
      name: 'Name',
      searchable: true,
      order: 2,
    },
    {
      key: 'node',
      name: 'Node name',
      searchable: true,
      order: 3,
    },
    {
      key: 'engineeringName',
      name: 'Engineering name',
      searchable: true,
      order: 4,
    },
    {
      key: 'settingRole',
      name: 'Role',
      searchable: true,
      order: 5,
    },
    {
      key: 'default_value',
      name: 'Default value',
      searchable: true,
      order: 6,
    },

    {
      key: 'unit',
      name: 'Unit',
      searchable: true,
      order: 7,
    },
    {
      key: 'previousValue',
      name: `Previous value (${entityName?.trim()})`,
      searchable: true,
      order: 8,
    },
    {
      key: 'dateChanged',
      name: 'Date changed',
      searchable: true,
      order: 9,
    },
    {
      key: 'description',
      name: 'Description',
      searchable: true,
      order: 10,
    },
    {
      key: 'default',
      name: 'Default',
      order: 11,
    },
  ];
};

export const getSettingsTableConfig = (
  entityName: string,
  dynamicOptions: Nullable<FarmOptionsTypes.Selection[]>,
  filteredOptions: SettingsTypes.FarmAstronaut[],
): TableTypes.TableRow[] => {
  if (!dynamicOptions) return [];

  if (dynamicOptions.length === 0) {
    return [
      {
        key: entityName,
        title: entityName,
        isEntityValueColumn: true,
      },
    ];
  }

  const sortedDynamicOptions = [...dynamicOptions].sort((a, b) => {
    if (a.order && b.order) {
      return a.order - b.order;
    }

    return a.order ? 1 : b.order ? -1 : 0;
  });

  const dynamicCols = [
    ...sortedDynamicOptions.map((item): TableTypes.TableRow => {
      return {
        key: item.key,
        title: item.name,
      };
    }),
  ];

  const orderIndex = entityName?.toLowerCase().includes('astronaut') ? 6 : 4;
  const lowestOrderToPushBefore = sortedDynamicOptions.filter((item) => {
    return item.order ? item.order > orderIndex : false;
  });

  const astronautOptions = [
    {
      key: entityName,
      id: entityName,
      title: entityName?.charAt(0).toUpperCase() + entityName.slice(1),
      isEntityValueColumn: true,
    },
    ...filteredOptions.map((item, index) => {
      return {
        key:
          (item.name?.toLocaleLowerCase().replace(/\s/g, '') ?? '') +
          `_${index}`,
        id: item.name?.toLocaleLowerCase().replace(/\s/g, ''),
        title: item.name?.charAt(0).toUpperCase() + item.name?.slice(1),
        isDynamicColumn: true,
        dynamicColumnIndex: index,
      };
    }),
  ];

  if (lowestOrderToPushBefore.length) {
    const indexToInsertTo = sortedDynamicOptions.findIndex((val) => {
      return val.key === lowestOrderToPushBefore[0].key;
    });

    dynamicCols.splice(indexToInsertTo, 0, ...astronautOptions);
  } else {
    dynamicCols.push(...astronautOptions);
  }

  return dynamicCols.filter((c) => c.key);
};

export const getFormattedDateText = (date, timeZone?: string) => {
  let formattedDate = timeZone
    ? formatInTimeZone(date, timeZone, UI_DATE_FORMAT)
    : format(new Date(date), UI_DATE_FORMAT);

  if (formattedDate.endsWith(' 24:')) {
    formattedDate = `${formattedDate.slice(0, -5)} 00${formattedDate.slice(
      -3,
    )}`;
  }

  return formattedDate;
};

export const DEFAULT_SETTINGS_COLUMN_WIDTHS: Partial<
  Record<keyof SettingsTypes.EntitySettingType, number>
> = {
  node: 100,
  engineeringName: 400,
  settingRole: 75,
  default_value: 140,
  unit: 75,
  description: 600,
  default: 75,
};

export const SETTING_ROLE_COLUMN_MATCH = 'settingRole';
